import { format, isSameDay, parseISO } from 'date-fns'
import { ru } from 'date-fns/locale'
import { plural } from './words'

/**
 * Работа со временем.
 *
 * Офис один и часовой пояс один, поэтому «местное время офиса» здесь — это местное время
 * браузера. В обмене с сервером всегда UTC в формате RFC 3339; преобразование в обе
 * стороны живёт только тут, чтобы дата не форматировалась в двадцати местах по-своему.
 *
 * Сетка получасовая. Единственное исключение из кратности — текущий получас: в 13:45
 * можно занять «сейчас — 14:30», иначе пятнадцать минут пропадают впустую.
 */

export const SLOT_MINUTES = 30
export const DEFAULT_DURATION_MINUTES = 60

export function parse(iso: string): Date {
  return parseISO(iso)
}

/** «13:45» — моноширинно, с табличными цифрами (класс .tnum на элементе). */
export function fmtTime(value: string | Date): string {
  return format(typeof value === 'string' ? parse(value) : value, 'HH:mm')
}

/** «13:45 – 14:30» одной строкой. */
export function fmtRange(startsAt: string, endsAt: string): string {
  return `${fmtTime(startsAt)} – ${fmtTime(endsAt)}`
}

/** «6 августа» */
export function fmtDate(value: string | Date): string {
  return format(typeof value === 'string' ? parse(value) : value, 'd MMMM', { locale: ru })
}

/**
 * «Четверг, 6 августа» — заголовок дня.
 *
 * Заглавная только первая буква, и делается это здесь, а не классом capitalize в вёрстке:
 * capitalize поднимает регистр у каждого слова и выдаёт «Четверг, 6 Августа» — месяц
 * с большой буквы по-русски неверен.
 */
export function fmtDayTitle(value: string | Date): string {
  const text = format(typeof value === 'string' ? parse(value) : value, 'EEEE, d MMMM', { locale: ru })
  return text.charAt(0).toUpperCase() + text.slice(1)
}

/** «6 августа, 13:45» — для писем и списков встреч. */
export function fmtDateTime(value: string | Date): string {
  const date = typeof value === 'string' ? parse(value) : value
  return `${fmtDate(date)}, ${fmtTime(date)}`
}

/** Ключ даты для запросов: 2026-08-06 в местном времени, а не в UTC. */
export function dateKey(date: Date): string {
  return format(date, 'yyyy-MM-dd')
}

export function fromDateKey(key: string): Date {
  const [year, month, day] = key.split('-').map(Number)
  return new Date(year, (month ?? 1) - 1, day ?? 1)
}

export function todayKey(): string {
  return dateKey(new Date())
}

export function isToday(key: string): boolean {
  return isSameDay(fromDateKey(key), new Date())
}

export function addDaysToKey(key: string, days: number): string {
  const date = fromDateKey(key)
  date.setDate(date.getDate() + days)
  return dateKey(date)
}

/** Разница в днях между ключами дат — для проверки горизонта бронирования. */
export function daysFromToday(key: string): number {
  const target = fromDateKey(key)
  const today = fromDateKey(todayKey())
  return Math.round((target.getTime() - today.getTime()) / 86_400_000)
}

export function toIso(date: Date): string {
  return date.toISOString()
}

export function minutesBetween(startsAt: string, endsAt: string): number {
  return Math.round((parse(endsAt).getTime() - parse(startsAt).getTime()) / 60_000)
}

export function addMinutes(iso: string, minutes: number): string {
  return toIso(new Date(parse(iso).getTime() + minutes * 60_000))
}

/** «09:00» + дата → момент. Рабочие часы в правилах — время на стене, а не момент. */
export function atWallTime(dateKeyValue: string, wallTime: string): Date {
  const [hours, minutes] = wallTime.split(':').map(Number)
  const date = fromDateKey(dateKeyValue)
  date.setHours(hours ?? 0, minutes ?? 0, 0, 0)
  return date
}

/** Ближайшие полчаса вперёд от указанного момента — значение по умолчанию в формах. */
export function nextHalfHour(from: Date = new Date()): Date {
  const date = new Date(from)
  date.setSeconds(0, 0)
  const minutes = date.getMinutes()
  date.setMinutes(minutes < 30 ? 30 : 60)
  return date
}

/** Список «09:00», «09:30», … в пределах рабочего дня — для выпадающего списка времени. */
export function wallTimeOptions(workStart: string, workEnd: string, step = SLOT_MINUTES): string[] {
  const [startHours, startMinutes] = workStart.split(':').map(Number)
  const [endHours, endMinutes] = workEnd.split(':').map(Number)
  const from = (startHours ?? 9) * 60 + (startMinutes ?? 0)
  const to = (endHours ?? 20) * 60 + (endMinutes ?? 0)
  const options: string[] = []
  for (let minutes = from; minutes <= to - step; minutes += step) {
    options.push(`${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`)
  }
  return options
}

export function pad(value: number): string {
  return String(value).padStart(2, '0')
}

/** «1 ч 30 мин» — длительность словами, без «90 минут». */
export function fmtDuration(minutes: number): string {
  if (minutes < 60) return `${minutes} мин`
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  const hoursWord = plural(hours, 'час', 'часа', 'часов')
  return rest === 0 ? `${hours} ${hoursWord}` : `${hours} ${hoursWord} ${rest} мин`
}

/**
 * Часы сервера против часов устройства.
 *
 * Отсчёты считаем от времени сервера: разошедшиеся часы дают тихо врущий таймер,
 * и никто не заподозрит подвох. serverTime приходит в каждом ответе расписания.
 */
export function clockOffset(serverTime: string | undefined): number {
  if (!serverTime) return 0
  const parsed = parse(serverTime).getTime()
  // Неразобранное время даёт NaN, а NaN расползается дальше молча: serverNow
  // возвращает Invalid Date, время в шапке превращается в «Invalid Date», а
  // красная черта на таймлайне встаёт в left: NaN% и пропадает с экрана.
  // Без поправки часы всего лишь местные — это честнее сломанного экрана.
  // Планшет в такой ситуации поступает так же (offsetFrom в kiosk/src/clock.ts):
  // одно правило на две сборки должно вести себя одинаково.
  return Number.isNaN(parsed) ? 0 : parsed - Date.now()
}

export function serverNow(offsetMs: number): Date {
  return new Date(Date.now() + offsetMs)
}
